import { useEffect, useState, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ApartmentViewContext } from "../context/ApartmentViewContext";
import { Context } from "../../../Context";
import {
	arrangeModeName,
	hotSpotModeName,
	infoModeName,
	insolationModeName,
	isometricModeName,
	stylesModeName,
} from "../context/contextStrings";
import { useGetScaleAfterRotation } from "../utils/useGetScaleAfterRotation";
import { getRotateToNorthAngleDeg, toIndex } from "../utils/geometryUtils";
import {
	appendSvgHeightWidth,
	appendSvgStyle,
	addPatternsDefs,
	changeBasicColorFills,
	changeElementsUseHref,
	reorderSvgCompartmentsGroup,
	changeElementsMarkersHref,
} from "../utils/svgUtils";
import PlanRendererInfoMode from "./PlanRendererInfoMode";
import PlanRendererInsolationMode from "./PlanRendererInsolationMode";
import PlanRendererTreeDMode from "./PlanRendererTreeDMode";
import PlanRendererArrangeMode from "./PlanRendererArrangeMode";
import PlanRendererHotSpotMode from "./PlanRendererHotSpotMode";
import PlanRendererIsometricMode from "./PlanRendererIsometricMode";
import CardinalsRenderer from "./CardinalsRenderer";
import AssetLoadError from "./AssetLoadError";
import BimLogo from "../../BimLogo";
import Loading from "../../Loading";
import styles from "../../../styles/aprt.module.scss";

function PlanViewer({
	currentArrangements, //TODO: should this be from context?
	arrangementVersions, //TODO: should this be from context?
	apartmentViewMode,
	insolationAngle,
	selectedCompartmentInd,
	hoveredCompartmentInd,
	projectStyle,
	dataUrl,
	currentEdifice,
	setApartmentViewInteractions,
	userSelectedArrangements,
	setUrlComboParam,
	compartmentsDictionary
}) {
	const { t } = useTranslation();
	const { isIsometryDragging } = useContext(ApartmentViewContext);
	const { 
		activeApartmentData, 
		projectSetup, 
		activeApartmentSelectedTier //this is an idea to handle multilevel apartments, it comes form context as user can access different levels of apartment from several components, for example a TiersView of whole building
	} = useContext(Context);

	// ========== HELPER FUNCTIONS ==========
	
	/**
	 * Returns cache key for storing/retrieving apartment plans.
	 * For multilevel apartments, each level gets its own cache entry.
	 * @param {string} uuid - Apartment UUID
	 * @param {number} refTier - Reference tier (floor level)
	 * @returns {string} Cache key in format "uuid_refTier"
	 */
	const getCacheKey = (uuid, refTier) => {
		return `${uuid}_${refTier}`;
	};

	const getDoubleDigitString = (num) => {
		return num.toString().padStart(2, "0");
	};

	const isFloorPlanMonochromatic = projectSetup.apartments.monochromatic_floorplan ?? true;
	const patternDefsIds = ["treeDStyle-default"]; //TODO: refactor to get patterns data form context when more 3d styles are added

	const svgStyleString = `
  .bg {
      fill: #fff;
      stroke: #000;
      stroke-width: 1.5px;
  }
  .tag {
    text-align: center;
    text-anchor: middle;
    font-size: 20px !important;
    pointer-events: none;
  }
`;
	const loadStatuses = {
		loading: "loading",
		success: "success",
		error: "error",
	};


	const [svgContent, setSvgContent] = useState(null);
	const [modifiedSvgContent, setModifiedSvgContent] = useState(null);
	const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });
	const [svgLoadingStatus, setSvgLoadingStatus] = useState(
		loadStatuses.loading
	);
	const [loadingErrorMessage, setSvgLoadingErrorMessage] = useState(null);

	const previousArrangementsRef = useRef(currentArrangements);
	const cachedApartmentPlansRef = useRef(new Map());
	const updatedSvgContentRef = useRef(null);
	const abortControllerRef = useRef(null);

	// Compute effective tier - determines which floor level to display
	// Uses activeApartmentSelectedTier if user selected specific level,
	// otherwise falls back to apartment's base ref_tier
	// Wrapped in useMemo to ensure stable value across renders
	const effectiveRefTier = useMemo(() => {
		if (activeApartmentSelectedTier !== null && activeApartmentSelectedTier !== undefined) {
			return activeApartmentSelectedTier;
		}
		return activeApartmentData?.ref_tier;
	}, [activeApartmentSelectedTier, activeApartmentData?.ref_tier]);

	// Cache key based on UUID + effective tier for multilevel support
	const currentCacheKey = activeApartmentData?.uuid && effectiveRefTier !== undefined
		? getCacheKey(activeApartmentData.uuid, effectiveRefTier)
		: null;

	const basePath = `${dataUrl}/apartments`;
	const threeDFileBasePath = `${dataUrl}/apartments/${activeApartmentData.uuid}`;
	
	// Build PNG filename for current tier (used for 3D texture patterns)
	// Note: Uses effectiveRefTier for multilevel apartment support
	const apartmentPngFileName = activeApartmentData && effectiveRefTier !== undefined
		? `${activeApartmentData.uuid}_${activeApartmentData.building_id}-00-${getDoubleDigitString(effectiveRefTier)}`
		: null;

	const isLoading = svgLoadingStatus === loadStatuses.loading;
	const isLoaded = svgLoadingStatus === loadStatuses.success;
	const isLoadError = svgLoadingStatus === loadStatuses.error;
	const isRotatedToNorth = apartmentViewMode.rotatedToNorth;
	const viewerMode = apartmentViewMode.mode;

	const rotationAngleDeg = getRotateToNorthAngleDeg(activeApartmentData.azimuth);
	const { scale, svgFrameRef } = useGetScaleAfterRotation(
		rotationAngleDeg,
		svgDimensions.height,
		svgDimensions.width
	);
	const showCardinals =
		viewerMode === insolationModeName || viewerMode === isometricModeName;

	const cardinalsStyle = {
		transform: `
          ${
						!isRotatedToNorth && viewerMode !== isometricModeName
							? `rotate(${-rotationAngleDeg}deg)`
							: ""
					}
      `,
		transition: "opacity 0.6s ease-in-out",
		opacity: 1,
		pointerEvents: `${viewerMode === insolationModeName ? "none" : "auto"}`,
	};

	if (viewerMode === isometricModeName) {
		cardinalsStyle.opacity = isIsometryDragging ? 1 : 0;
		cardinalsStyle.pointerEvents = isIsometryDragging ? "auto" : "none";
	}

	const getTripleDigitString = (tier) => {
		return tier.toString().padStart(3, "0");
	};


	const logInRed = (message, constant) => {
		console.log(`%c${message}`, "color: red;", constant ? constant : "");
	};

	const decodeSvg = (arrayBuffer) => {
		let svgText;

		// Detect encoding
		const utf16LEBOM = new Uint8Array([0xff, 0xfe]);
		const utf16BOM = new Uint8Array(arrayBuffer.slice(0, 2));

		if (utf16BOM[0] === utf16LEBOM[0] && utf16BOM[1] === utf16LEBOM[1]) {
			// UTF-16 LE
			const decoder = new TextDecoder("utf-16le");
			svgText = decoder.decode(arrayBuffer);
		} else {
			// Default to UTF-8
			const decoder = new TextDecoder("utf-8");
			svgText = decoder.decode(arrayBuffer);
		}

		return svgText;
	};



	const runStandardSvgModifiers = (svgElement, apartmentFileName) => {
		if (!svgElement) return;

		const viewBox = svgElement.getAttribute("viewBox");
		if (viewBox) {
			const [minX, minY, width, height] = viewBox.split(" ").map(Number);
			setSvgDimensions({ width, height });
		}

		appendSvgHeightWidth(svgElement);
		appendSvgStyle(svgElement, svgStyleString);
		// addPatternsDefs(svgElement, basePath, apartmentFileName, patternDefsIds);
		addPatternsDefs(svgElement, threeDFileBasePath, apartmentPngFileName, patternDefsIds);
		changeBasicColorFills(svgElement, projectStyle, isFloorPlanMonochromatic);
		changeElementsUseHref(svgElement, basePath);
		changeElementsMarkersHref(svgElement, basePath);
		reorderSvgCompartmentsGroup(svgElement);

		return svgElement;
	};

	//fetching default svg
	const loadDefaultSvg = async (signal, apartmentFileName) => {
		setSvgLoadingStatus(loadStatuses.loading);
		// console.log('%cLoading default SVG plan', 'color: blue;');
		try {
			//TODO: ref_tier for multilevel apartments is handled by ApartmentView.jsx in apartmentPath
			//TODO: is legacy name 'apartmentPath' misleading now that its only uuid_ref_tier? maybe rename?
			const svgFetchURL = `${basePath}/${activeApartmentData.uuid}/${apartmentFileName}.svg`;
			const response = await fetch(svgFetchURL, { signal });

			if (!response.ok) {
				console.error("Error loading SVG plan:", response.statusText);
				setSvgLoadingErrorMessage(t("svg_load_error_1"));
				setSvgLoadingStatus(loadStatuses.error);
				return;
			}

			const contentType = response.headers.get("Content-Type");
			if (contentType !== "image/svg+xml") {
				console.log(
					"%cUnexpected content type in fetched file :",
					"color: red;",
					contentType
				);
			}

			const arrayBuffer = await response.arrayBuffer();
			const svgText = decodeSvg(arrayBuffer);

			if (!svgText.startsWith("<svg") && !svgText.startsWith("<?xml")) {
				console.error(`Invalid SVG content`);
				setSvgLoadingErrorMessage(t("svg_load_error_1"));
				setSvgLoadingStatus(loadStatuses.error);
				return;
			}

			const parser = new DOMParser();
			const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
			const parseError = svgDoc.querySelector("parsererror");
			if (parseError) {
					console.error("loadDefaultSvg: DOMParser error:", parseError.textContent);
			}
									
			const svgElement = svgDoc.querySelector("svg");


			const modifiedSvg = runStandardSvgModifiers(svgElement, apartmentFileName);
			// console.log("%cNew SVG plan appended and loaded", "color: green");

			return modifiedSvg;
		} catch (err) {
			if (err.name !== "AbortError") {
				setSvgLoadingErrorMessage(t("svg_load_error_1"));
				setSvgLoadingStatus(loadStatuses.error);
				console.error("Error loading SVG plan", err);
			}
			console.log("%cFetch aborted by user reload", "color: gray");
		}
	};

	//pobieranie plików svg z wersjami aranżacji z serwera
	// Fetches ALL versions for ALL compartments (all tiers) from ${uuid}/versions/ folder
	// Cache is shared between tiers - fetch once, use for all levels
	const fetchAndParseVersionsSVGs = async (apartmentDirectory, localUuid) => {
		const fetchedContents = await Promise.all(
			arrangementVersions.map(async (numberOfVersions, index) => {
				const compartmentNumber = index + 1;
				const versionsContents = await Promise.all(
					Array.from({ length: numberOfVersions - 1 }, (_, i) => i + 1).map(
						async (version) => {
							const fileName = `${localUuid}_${getDoubleDigitString(
								compartmentNumber
							)}_${getDoubleDigitString(version)}.svg`;

							try {
								const versionSVGsDir = `${basePath}/${apartmentDirectory}/versions`;
								const response = await fetch(`${versionSVGsDir}/${fileName}`);
								if (!response.ok) {
									console.log(
										logInRed(
											`Failed to fetch ${activeApartmentData.uuid} / ${fileName}; ${response.statusText}`
										)
									);
									return { version: version, content: null };
								}

								const contentType = response.headers.get("Content-Type");
								const arrayBuffer = await response.arrayBuffer();
								const svgText = decodeSvg(arrayBuffer);

								if (contentType !== "image/svg+xml") {
									console.log(
										logInRed(
											`Unexpected content type for ${versionSVGsDir}/${fileName}: ${contentType}`
										)
									);
									if (
										!svgText.startsWith("<svg") &&
										!svgText.startsWith("<?xml")
									) {
										console.log(
											logInRed(
												`Invalid SVG content in ${activeApartmentData.uuid} / ${fileName}`
											)
										);
										return { version: version, content: null };
									}
								}

								const parser = new DOMParser();
								const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
								const svgElement = svgDoc.querySelector("svg");

								if (svgElement) {
									changeElementsUseHref(svgElement, basePath);

									let compartmentGroup = svgElement.querySelector(
										`g[number="${compartmentNumber}"]`
									);
									if (!compartmentGroup) {
										compartmentGroup = document.createElementNS(
											"http://www.w3.org/2000/svg",
											"g"
										);
										compartmentGroup.setAttribute("number", compartmentNumber);
										compartmentGroup.innerHTML = svgElement.innerHTML;
										svgElement.innerHTML = "";
										svgElement.appendChild(compartmentGroup);
									}

									console.log(`%cFetched version SVG`, "color: green;");
									return { version: version, content: svgElement.outerHTML };
								} else {
									console.log(
										logInRed(
											`SVG element not found in ${activeApartmentData.uuid} / ${fileName}`
										)
									);
									return { version: version, content: null };
								}
							} catch (error) {
								console.log(
									logInRed(
										`Error while loading or reading version file ${activeApartmentData.uuid} / ${fileName}`
									),
									error
								);
								//TODO: do we wanto to handle error cases for versions fetch in front end - display some other error message? a popup?
								return { version: version, content: null };
							}
						}
					)
				);

				return {
					number: compartmentNumber,
					versions: versionsContents,
				};
			})
		);

		return fetchedContents;
	};

	//TODO: move this to utils or to useEffect that initializes them?
	const mergeDefaultAndVersionedArrangements = (
		cachedVersionsData,
		fetchedVersions,
		defaultArrangements
	) => {
		const updatedCache = [...cachedVersionsData];

		const mergeVersions = (existingCompartment, newCompartment) => {
			newCompartment.versions.forEach((newVersion) => {
				const existingVersionIndex = existingCompartment.versions.findIndex(
					(v) => v.version === newVersion.version
				);

				if (existingVersionIndex !== -1) {
					existingCompartment.versions[existingVersionIndex] = newVersion;
				} else {
					existingCompartment.versions.push(newVersion);
				}
			});
		};

		const mergeCompartments = (compartments) => {
			compartments.forEach((newCompartment) => {
				const existingCompartment = updatedCache.find(
					(c) => c.number === newCompartment.number
				);

				if (!existingCompartment) {
					updatedCache.push(newCompartment);
				} else {
					mergeVersions(existingCompartment, newCompartment);
				}
			});
		};

		mergeCompartments(defaultArrangements);
		mergeCompartments(fetchedVersions);

		const sortedState = updatedCache.sort((a, b) => a.number - b.number);

		return sortedState;
	};

	const getDefaultCompartmentArrangementFromSvg = (svgElement) => {
		const versionContents = [];

		const compartmentsGroup = svgElement.querySelectorAll(".compartments");
		for (const group of compartmentsGroup) {
			const compartments = group.querySelectorAll("g[number]");
			for (const compartment of compartments) {
				const compartmentNumber = compartment.getAttribute("number");
				const clonedCompartment = compartment.cloneNode(true);
				const paths = Array.from(clonedCompartment.children).filter(
					(child) => child.tagName === "path"
				);
				paths.forEach((path) => path.remove());

				const wrapperSvg = document.createElementNS(
					"http://www.w3.org/2000/svg",
					"svg"
				);
				wrapperSvg.appendChild(clonedCompartment);

				const groupContent = wrapperSvg.outerHTML;

				versionContents.push({
					number: parseInt(compartmentNumber),
					versions: [
						{
							version: 0,
							content: groupContent,
						},
					],
				});
			}
		}

		return versionContents;
	};

	const replaceSvgCompartmentContent = (
		cachedState,
		currentSvgContent,
		compartmentNumber,
		version
	) => {
		const compartmentElementInCache = cachedState.find(
			(c) => c.number === compartmentNumber
		);
		if (!compartmentElementInCache) {
			console.log(
				`warning: cache for compartment ${compartmentNumber} not found`
			);
			return currentSvgContent;
		} //TODO: handle this case, why there is no compartment in state?

		const versionContent = compartmentElementInCache.versions.find(
			(v) => v.version === version
		);

		if (versionContent === undefined) {
			console.log(
				`warning: version: '${version}' for compartment: '${compartmentNumber}' has undefined content`
			);
			return currentSvgContent;
		} else if (!versionContent || !versionContent.content) {
			console.log(
				`warning: version: '${version}' for compartment: '${compartmentNumber}' not found in cache; content: '${versionContent.content}'`
			);
			return currentSvgContent;
		} //TODO: handle this case: if no content then use current content, and log to console

		const parser = new DOMParser();
		const newSvgElement = parser.parseFromString(
			versionContent.content,
			"image/svg+xml"
		);
		if (!newSvgElement) return currentSvgContent;

		const currentCompartmentGroup = currentSvgContent.querySelector(
			`.compartments > g[number="${compartmentNumber}"]`
		);

		if (currentCompartmentGroup) {
			const currentTag = currentCompartmentGroup.querySelector("text.tag");
			const newTag = newSvgElement.querySelector("text.tag");
			if (currentTag && newTag) {
				currentTag.setAttribute("transform", newTag.getAttribute("transform"));
			}

			const currentEquipments =
				currentCompartmentGroup.querySelector("g.equipments");
			const newEquipments = newSvgElement.querySelector("g.equipments");
			const currentCaseworks =
				currentCompartmentGroup.querySelector("g.caseworks");
			const newCaseworks = newSvgElement.querySelector("g.caseworks");

			//HANDLE CASEWORKS FIRST
			if (currentCaseworks && newCaseworks) {
				currentCompartmentGroup.removeChild(currentCaseworks);
				currentCompartmentGroup.insertBefore(newCaseworks, currentEquipments);
			} else if (newCaseworks) {
				if (currentCaseworks) {
					currentCompartmentGroup.removeChild(currentCaseworks);
				}
				currentCompartmentGroup.insertBefore(newCaseworks, currentEquipments);
			} else if (currentCaseworks) {
				currentCompartmentGroup.removeChild(currentCaseworks);
			}
			//HANDLE EQUIPMENTS
			if (currentEquipments && newEquipments) {
				currentCompartmentGroup.replaceChild(newEquipments, currentEquipments);
			}
		}

		return currentSvgContent;
	};

	const updateSvgContent = async () => {
		const { compartmentsAllArrangements } =
			cachedApartmentPlansRef.current.get(currentCacheKey);

		// Get sorted compartments for the current arrangement mapping
		// currentArrangements indices correspond to sorted compartments
		const sortedCompartments = activeApartmentData.compartments
			.sort((a, b) => parseInt(a.number) - parseInt(b.number));

		let updatedSvgContent = updatedSvgContentRef.current.cloneNode(true);

		currentArrangements.forEach((arrangement, index) => {
			if (arrangement !== previousArrangementsRef.current[index]) {
				const compartmentNumber = index + 1;
				// // Map index to actual compartment number
				// const compartmentNumber = parseInt(sortedCompartments[index].number);
				updatedSvgContent = replaceSvgCompartmentContent(
					compartmentsAllArrangements,
					updatedSvgContent,
					compartmentNumber,
					arrangement
				);
			}
		});

		setModifiedSvgContent(updatedSvgContent.outerHTML);
		setSvgLoadingStatus(loadStatuses.success);
		updatedSvgContentRef.current = updatedSvgContent;
		previousArrangementsRef.current = currentArrangements;
	};

	// Update SVG when arrangements change (furniture versions)
	useEffect(() => {
		if (!currentCacheKey || !cachedApartmentPlansRef.current.has(currentCacheKey) || !svgContent) {
			// No cache key, not in cache, or no base SVG loaded yet
			return;
		}

		// Validate that updatedSvgContentRef is cloneable
		if (!updatedSvgContentRef.current || typeof updatedSvgContentRef.current.cloneNode !== 'function') {
			console.warn("updateSvgContent: updatedSvgContentRef.current is not a valid node for cloning. Attempting recovery.");
			
			// Try to recover from svgContent if available
			if (svgContent && typeof svgContent.cloneNode === 'function') {
				updatedSvgContentRef.current = svgContent.cloneNode(true);
			} else {
				return; // Cannot proceed
			}
		}

		updateSvgContent();
	}, [
		currentArrangements, 
		svgContent,
		currentCacheKey, // Changed from activeApartmentData?.uuid - now includes tier
		effectiveRefTier, // Added: trigger re-render when tier changes
	]);

	// Main effect: Load SVG data for current apartment and tier
	useEffect(() => {
		abortControllerRef.current = new AbortController();
		const { signal } = abortControllerRef.current;

		// Validate activeApartmentData completeness
		if (!activeApartmentData || !activeApartmentData.uuid || typeof effectiveRefTier === 'undefined') {
			console.warn("PlanViewer: useEffect (loadSvgData) - activeApartmentData or effectiveRefTier incomplete. Setting error.");
			setSvgLoadingStatus(loadStatuses.error);
			setSvgLoadingErrorMessage(t(""));
			return; // Don't execute cleanup since nothing was started
		}

		const localUuid = activeApartmentData.uuid;
		const localRefTier = effectiveRefTier; // Use effectiveRefTier instead of activeApartmentData.ref_tier
		const localCacheKey = getCacheKey(localUuid, localRefTier);
		
		// Build file paths for this specific tier
		const apartmentFileName = `${localUuid}_${activeApartmentData.building_id}-00-${getDoubleDigitString(localRefTier)}`;
		const apartmentDirectory = `${localUuid}`;

		const loadSvgData = async () => {
			setSvgLoadingStatus(loadStatuses.loading);

			// Control cache size
			if (cachedApartmentPlansRef.current.size >= 50) {
				const firstKey = cachedApartmentPlansRef.current.keys().next().value;
				cachedApartmentPlansRef.current.delete(firstKey);
			}

			// Check if this tier is already cached
			const isThisTierCached = cachedApartmentPlansRef.current.has(localCacheKey);
			const cachedPlanData = isThisTierCached ? cachedApartmentPlansRef.current.get(localCacheKey) : null;
			const cachedVersionsData = cachedPlanData?.compartmentsAllArrangements || [];

			let defaultSvg = null;

			// Try to get from cache first
			if (isThisTierCached) {
				if (cachedPlanData && cachedPlanData.defaultSvg && typeof cachedPlanData.defaultSvg.cloneNode === 'function') {
					defaultSvg = cachedPlanData.defaultSvg;
				} else {
					console.warn(`loadSvgData: Cache for key ${localCacheKey} existed, but its defaultSvg was invalid. Will reload.`);
				}
			}

			// Load from server if not in cache
			if (!defaultSvg) {
				try {
					defaultSvg = await loadDefaultSvg(signal, apartmentFileName);
				} catch (error) {
					console.error(`loadSvgData: Error during loadDefaultSvg for ${localCacheKey}`, error);
					if (error.name !== 'AbortError') {
						setSvgLoadingStatus(loadStatuses.error);
					}
				}
			}

			// Validate loaded SVG
			if (!defaultSvg || typeof defaultSvg.cloneNode !== 'function') {
				console.warn(`PlanViewer: defaultSvg not available for ${localCacheKey}. Value:`, defaultSvg);
				
				if (signal.aborted) {
					console.log(`PlanViewer: Load for ${localCacheKey} was aborted.`);
				} else if (svgLoadingStatus !== loadStatuses.error) {
					setSvgLoadingStatus(loadStatuses.error);
					setSvgLoadingErrorMessage(`${t("svg_load_error_1")} ${t("load_error_1")}`);
				}
				return; 
			}

			updatedSvgContentRef.current = defaultSvg.cloneNode(true);
			
			// Load arrangement versions if not cached
			if (defaultSvg && cachedVersionsData.length === 0) {
				const fetchedVersionsData = await fetchAndParseVersionsSVGs(apartmentDirectory, localUuid);

				const compartmentsDefaultArrangements =
					getDefaultCompartmentArrangementFromSvg(defaultSvg);
				const compartmentsAllArrangements =
					mergeDefaultAndVersionedArrangements(
						[],
						fetchedVersionsData,
						compartmentsDefaultArrangements
					);

				// Store in cache with tier-specific key
				cachedApartmentPlansRef.current.set(localCacheKey, {
					defaultSvg,
					compartmentsAllArrangements,
				});
			} else if (!cachedPlanData?.defaultSvg) {
				// If defaultSvg was reloaded but versions were already cached
				cachedApartmentPlansRef.current.set(localCacheKey, {
					defaultSvg,
					compartmentsAllArrangements: cachedVersionsData,
				});
			}

			setSvgContent(defaultSvg);

			// Initial render with default arrangements
			if (!previousArrangementsRef.current || previousArrangementsRef.current.every(arr => arr === 0 || arr === undefined)) {
				setModifiedSvgContent(defaultSvg.outerHTML);
				setSvgLoadingStatus(loadStatuses.success);
				updatedSvgContentRef.current = defaultSvg.cloneNode(true); 
				previousArrangementsRef.current = currentArrangements; 
			}
		};

		loadSvgData().catch(err => {
			console.error("PlanViewer: Unhandled error in loadSvgData promise chain", err);
			if (err.name !== 'AbortError' && !signal.aborted) {
				setSvgLoadingErrorMessage(t("svg_load_error_1"));
				setSvgLoadingStatus(loadStatuses.error);
			}
		});

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [
		activeApartmentData, // Triggers when apartment changes
		effectiveRefTier, // Triggers when tier selection changes (multilevel support)
	]);


	return (
		<>
			{isLoading && <Loading />}
			{isLoadError && (
				<AssetLoadError
					dataUrl={dataUrl}
					loadingErrorMessage={loadingErrorMessage}
				/>
			)}
			{isLoaded && (
				<div className={styles.svgPlan__frame} ref={svgFrameRef}>
					<div
						className={styles.svgPlan__container}
						style={{
							transform: `
                        ${
													isRotatedToNorth
														? `rotate(${rotationAngleDeg}deg) scale(${scale})`
														: ""
												}
                        `,
							display: `
                        ${viewerMode !== isometricModeName ? "flex" : "none"}
                        `,
						}}
					>
						<>
							{viewerMode === infoModeName && (
								<PlanRendererInfoMode
									svgContent={modifiedSvgContent}
									setApartmentViewInteractions={setApartmentViewInteractions}
									selectedCompartmentInd={selectedCompartmentInd}
									hoveredCompartmentInd={hoveredCompartmentInd}
								/>
							)}
							{viewerMode === arrangeModeName && (
								<PlanRendererArrangeMode
									svgContent={modifiedSvgContent}
									apartmentData={activeApartmentData}
									userSelectedArrangements={userSelectedArrangements}
									setApartmentViewInteractions={setApartmentViewInteractions}
									selectedCompartmentInd={selectedCompartmentInd}
									hoveredCompartmentInd={hoveredCompartmentInd}
								/>
							)}
							{viewerMode === stylesModeName && (
								<PlanRendererTreeDMode
									svgContent={modifiedSvgContent}
									patternDefsIds={patternDefsIds}
									setApartmentViewInteractions={setApartmentViewInteractions}
									selectedCompartmentInd={selectedCompartmentInd}
									hoveredCompartmentInd={hoveredCompartmentInd}
								/>
							)}
							{viewerMode === hotSpotModeName && (
								<PlanRendererHotSpotMode
									svgContent={modifiedSvgContent}
									apartmentData={activeApartmentData}
									setApartmentViewInteractions={setApartmentViewInteractions}
									selectedCompartmentInd={selectedCompartmentInd}
									hoveredCompartmentInd={hoveredCompartmentInd}
									dataUrl={dataUrl}
								/>
							)}
							{viewerMode === insolationModeName && (
								<PlanRendererInsolationMode
									svgContent={modifiedSvgContent}
									svgDimensions={svgDimensions}
									rotationAngleDeg={rotationAngleDeg}
									insolationAngle={insolationAngle}
									apartmentData={activeApartmentData}
									setApartmentViewInteractions={setApartmentViewInteractions}
									selectedCompartmentInd={selectedCompartmentInd}
									hoveredCompartmentInd={hoveredCompartmentInd}
									compartmentsDictionary={compartmentsDictionary}
								/>
							)}
						</>
						{/* )} */}
					</div>
					<div
						className={styles.svgPlan__container}
						style={{
							display: `
                        ${viewerMode === isometricModeName ? "flex" : "none"}
                        `,
						}}
					>
						{viewerMode === isometricModeName && (
							<PlanRendererIsometricMode
								svgContent={modifiedSvgContent}
								apartmentData={activeApartmentData}
								setApartmentViewInteractions={setApartmentViewInteractions}
								selectedCompartmentInd={selectedCompartmentInd}
								hoveredCompartmentInd={hoveredCompartmentInd}
								dataUrl={dataUrl}
								compartmentsDictionary={compartmentsDictionary} 
							/>
						)}
					</div>
					{showCardinals || isRotatedToNorth ? (
						<div
							className={`${styles.svgPlan__container} ${styles.overlaySVG}`}
							style={cardinalsStyle}
						>
							<CardinalsRenderer
								viewerMode={viewerMode}
								apartmentData={activeApartmentData}
							/>
						</div>
					) : null}
					<div className={styles.overlay_logo}>
						<BimLogo />
					</div>
				</div>
			)}
		</>
	);
}

export default PlanViewer;
